import Papa from 'papaparse';
import * as XLSX from 'xlsx-js-style';
import pkg from 'file-saver';
const {saveAs} = pkg;

/**
 * Generate a standardized filename for exports
 * @param {Object} options - Configuration options
 * @param {string} options.protocolNumber - Study protocol number
 * @param {string} [options.siteId] - Site ID (optional)
 * @param {string} [options.subjectId] - Subject ID (optional)
 * @param {string} options.reportType - Type of report (e.g., 'CGM_Stats', 'Compliance', 'SMBG')
 * @param {Date} [options.timestamp] - Timestamp for the export (defaults to current time)
 * @returns {string} Formatted filename
 */
export function generateExportFilename({ protocolNumber, siteId, subjectId, reportType, timestamp = new Date() }) {
  const parts = [];
  
  // Always start with protocol number
  parts.push(protocolNumber || 'Unknown');
  
  // Add site ID if provided
  if (siteId) {
    parts.push(siteId);
  }
  
  // Add subject ID if provided
  if (subjectId) {
    parts.push(subjectId);
  }
  
  // Add report type
  parts.push(reportType);
  
  // Add timestamp (YYYY-MM-DD_HH-MM format)
  const dateStr = timestamp.toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
  parts.push(dateStr);
  
  return parts.join('_');
}

// Function to export data to CSV
export function exportToCSV(data, fileName) {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${fileName}.csv`);
}

// Function to export data to Excel
export function exportToExcel(data, fileName) {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  
  // Get the range of the worksheet
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  
  // Enable filtering for all columns
  worksheet['!autofilter'] = { ref: worksheet['!ref'] };
  
  // Calculate column widths
  const columnWidths = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let maxLength = 0;
    
    // Check header length
    const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    const headerCell = worksheet[headerAddress];
    if (headerCell && headerCell.v) {
      maxLength = String(headerCell.v).length;
    }
    
    // Check data lengths
    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (cell && cell.v) {
        const length = String(cell.v).length;
        if (length > maxLength) {
          maxLength = length;
        }
      }
    }
    
    // Add some padding and set the width
    columnWidths[C] = maxLength + 2;
  }
  
  // Set column widths
  worksheet['!cols'] = columnWidths.map(width => ({ width }));
  
  // Style headers and alternate rows
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cell_address]) worksheet[cell_address] = { v: '' };
      
      if (R === 0) {
        // Header styling
        worksheet[cell_address].s = {
          font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "555555" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: 'thin', color: { rgb: "000000" } },
            bottom: { style: 'thin', color: { rgb: "000000" } },
            left: { style: 'thin', color: { rgb: "000000" } },
            right: { style: 'thin', color: { rgb: "000000" } }
          }
        };
      } else {
        // Alternating row styling
        worksheet[cell_address].s = {
          fill: {
            fgColor: { rgb: R % 2 === 0 ? "FFFFFF" : "F3F3F3" }
          },
          alignment: { vertical: "center" }, // Center align all data vertically
          border: {
            top: { style: 'thin', color: { rgb: "CCCCCC" } },
            bottom: { style: 'thin', color: { rgb: "CCCCCC" } },
            left: { style: 'thin', color: { rgb: "CCCCCC" } },
            right: { style: 'thin', color: { rgb: "CCCCCC" } }
          }
        };
      }
    }
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

// Traffic light color mapping for Excel
const TRAFFIC_LIGHT_COLORS = {
  'RED': { fgColor: { rgb: 'EF4444' } },      // Red
  'YELLOW': { fgColor: { rgb: 'FACC15' } },   // Yellow
  'GREEN': { fgColor: { rgb: '22C55E' } },    // Green
  'GRAY': { fgColor: { rgb: '9CA3AF' } }      // Gray
};

const INVALID_RANGE_TEXT = 'Invalid Range';

/**
 * Export compliance report data to Excel with colored traffic light cells
 * @param {Array<Object>} data - The data to export
 * @param {string} fileName - The desired name of the exported file
 * @param {Array<string>} trafficLightColumns - Column names that should be colored as traffic lights
 */
export function exportComplianceReportToExcel(data, fileName, trafficLightColumns = []) {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  
  // Get the range of the worksheet
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  
  // Enable filtering for all columns
  worksheet['!autofilter'] = { ref: worksheet['!ref'] };
  
  // Get header names and their column indices
  const headers = {};
  for (let C = range.s.c; C <= range.e.c; C++) {
    const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    const headerCell = worksheet[headerAddress];
    if (headerCell && headerCell.v) {
      headers[headerCell.v] = C;
    }
  }
  
  // Calculate column widths
  const columnWidths = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let maxLength = 0;
    
    // Check header length
    const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    const headerCell = worksheet[headerAddress];
    if (headerCell && headerCell.v) {
      maxLength = String(headerCell.v).length;
    }
    
    // Check data lengths
    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (cell && cell.v) {
        const length = String(cell.v).length;
        if (length > maxLength) {
          maxLength = length;
        }
      }
    }
    
    // Add some padding and set the width
    columnWidths[C] = maxLength + 2;
  }
  
  // Set column widths
  worksheet['!cols'] = columnWidths.map(width => ({ width }));
  
  // Get column indices for traffic light columns
  const trafficLightColumnIndices = trafficLightColumns
    .map(colName => headers[colName])
    .filter(idx => idx !== undefined);
  
  // Style headers and data rows
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cell_address]) worksheet[cell_address] = { v: '' };
      
      if (R === 0) {
        // Header styling
        worksheet[cell_address].s = {
          font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "555555" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: 'thin', color: { rgb: "000000" } },
            bottom: { style: 'thin', color: { rgb: "000000" } },
            left: { style: 'thin', color: { rgb: "000000" } },
            right: { style: 'thin', color: { rgb: "000000" } }
          }
        };
      } else {
        const isTrafficLightColumn = trafficLightColumnIndices.includes(C);
        const cellValue = worksheet[cell_address].v;
        const isInvalidRange = String(cellValue) === INVALID_RANGE_TEXT;
        
        let fillColor = { fgColor: { rgb: R % 2 === 0 ? "FFFFFF" : "F3F3F3" } };
        let fontColor = { color: { rgb: "000000" } };
        
        if (isInvalidRange) {
          fillColor = { fgColor: { rgb: 'FEE2E2' } };
          fontColor = { color: { rgb: 'DC2626' }, bold: true };
        } else if (isTrafficLightColumn && cellValue) {
          const colorKey = String(cellValue).toUpperCase();
          if (TRAFFIC_LIGHT_COLORS[colorKey]) {
            fillColor = TRAFFIC_LIGHT_COLORS[colorKey];
            if (colorKey === 'RED' || colorKey === 'GREEN' || colorKey === 'GRAY') {
              fontColor = { color: { rgb: "FFFFFF" }, bold: true };
            } else {
              fontColor = { color: { rgb: "000000" }, bold: true };
            }
          }
        }
        
        worksheet[cell_address].s = {
          font: { sz: 11, ...fontColor },
          fill: fillColor,
          alignment: { horizontal: (isTrafficLightColumn || isInvalidRange) ? "center" : "left", vertical: "center" },
          border: {
            top: { style: 'thin', color: { rgb: "CCCCCC" } },
            bottom: { style: 'thin', color: { rgb: "CCCCCC" } },
            left: { style: 'thin', color: { rgb: "CCCCCC" } },
            right: { style: 'thin', color: { rgb: "CCCCCC" } }
          }
        };
      }
    }
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Compliance Report');
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

// Column color mapping for CGM Sensor Statistics Excel export
const SENSOR_STATS_COLUMN_COLORS = {
  TBR: { fgColor: { rgb: 'FEF3C7' } },      // Amber-100 (light amber)
  TIR: { fgColor: { rgb: 'DCFCE7' } },      // Green-100 (light green)  
  TAR: { fgColor: { rgb: 'FEE2E2' } }       // Red-100 (light red)
};

const SENSOR_STATS_HEADER_COLORS = {
  TBR: { fgColor: { rgb: 'F59E0B' } },      // Amber-500 (amber header)
  TIR: { fgColor: { rgb: '22C55E' } },      // Green-500 (green header)
  TAR: { fgColor: { rgb: 'EF4444' } }       // Red-500 (red header)
};

/**
 * Export sensor statistics data to Excel with colored TBR, TIR, TAR columns
 * @param {Array<Object>} data - The data to export
 * @param {string} fileName - The desired name of the exported file
 */
export function exportSensorStatisticsToExcel(data, fileName) {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  
  // Get the range of the worksheet
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  
  // Enable filtering for all columns
  worksheet['!autofilter'] = { ref: worksheet['!ref'] };
  
  // Get header names and their column indices
  const headers = {};
  for (let C = range.s.c; C <= range.e.c; C++) {
    const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    const headerCell = worksheet[headerAddress];
    if (headerCell && headerCell.v) {
      headers[headerCell.v] = C;
    }
  }
  
  // Determine which columns get which colors
  const columnColorMap = {};
  for (const [headerName, colIndex] of Object.entries(headers)) {
    if (headerName.includes('TBR')) {
      columnColorMap[colIndex] = 'TBR';
    } else if (headerName.includes('TIR')) {
      columnColorMap[colIndex] = 'TIR';
    } else if (headerName.includes('TAR')) {
      columnColorMap[colIndex] = 'TAR';
    }
  }
  
  // Calculate column widths
  const columnWidths = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let maxLength = 0;
    
    // Check header length
    const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    const headerCell = worksheet[headerAddress];
    if (headerCell && headerCell.v) {
      maxLength = String(headerCell.v).length;
    }
    
    // Check data lengths
    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (cell && cell.v) {
        const length = String(cell.v).length;
        if (length > maxLength) {
          maxLength = length;
        }
      }
    }
    
    // Add some padding and set the width
    columnWidths[C] = maxLength + 2;
  }
  
  // Set column widths
  worksheet['!cols'] = columnWidths.map(width => ({ width }));
  
  // Style headers and data rows
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cell_address]) worksheet[cell_address] = { v: '' };
      
      const colorType = columnColorMap[C];
      
      if (R === 0) {
        // Header styling
        let headerFill = { fgColor: { rgb: "555555" } };
        let headerFontColor = { rgb: "FFFFFF" };
        
        // Use colored headers for TBR/TIR/TAR columns
        if (colorType && SENSOR_STATS_HEADER_COLORS[colorType]) {
          headerFill = SENSOR_STATS_HEADER_COLORS[colorType];
          headerFontColor = { rgb: "FFFFFF" };
        }
        
        worksheet[cell_address].s = {
          font: { bold: true, sz: 12, color: headerFontColor },
          fill: headerFill,
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: 'thin', color: { rgb: "000000" } },
            bottom: { style: 'thin', color: { rgb: "000000" } },
            left: { style: 'thin', color: { rgb: "000000" } },
            right: { style: 'thin', color: { rgb: "000000" } }
          }
        };
      } else {
        // Data row styling
        let fillColor = { fgColor: { rgb: R % 2 === 0 ? "FFFFFF" : "F3F3F3" } };
        
        // Use colored backgrounds for TBR/TIR/TAR columns
        if (colorType && SENSOR_STATS_COLUMN_COLORS[colorType]) {
          fillColor = SENSOR_STATS_COLUMN_COLORS[colorType];
        }
        
        worksheet[cell_address].s = {
          font: { sz: 11, color: { rgb: "000000" } },
          fill: fillColor,
          alignment: { vertical: "center" },
          border: {
            top: { style: 'thin', color: { rgb: "CCCCCC" } },
            bottom: { style: 'thin', color: { rgb: "CCCCCC" } },
            left: { style: 'thin', color: { rgb: "CCCCCC" } },
            right: { style: 'thin', color: { rgb: "CCCCCC" } }
          }
        };
      }
    }
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, 'CGM Sensor Statistics');
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

/**
 * Function to export data to CSV with customized column titles,
 * additional columns, and formatted timestamps.
 *
 * @param {Array<Object>} data - The data to export.
 * @param {string} fileName - The desired name of the exported file.
 * @param {string} timezone - The timezone to format the timestamps (e.g., 'America/New_York').
 * @param {string} subjectId - The subject ID to add to each row.
 */
export function exportCGMSensorLogToCSV(data, fileName, timezone = 'UTC', subjectId = '00000000') {

  //console.log("timezone:", timezone);

  // Define the desired column order and titles
  const columnTitles = [
    { key: 'subjectId', title: 'Subject ID' },
    { key: 'day', title: 'Day' },
    { key: 'externalId', title: 'Sensor ID' },
    { key: 'location', title: 'Location' },
    { key: 'minTimestamp', title: 'From (Local)' },
    { key: 'maxTimestamp', title: 'To (Local)' },
    { key: 'count', title: 'Record Count' }
  ];

  // Process the data
  const processedData = data.map(row => {
    // Add the subjectId
    const newRow = { ...row, subjectId };

    // Format the timestamp to the specified timezone
    if (newRow.minTimestamp) {
      const date = new Date(newRow.minTimestamp);
      newRow.minTimestamp = date.toLocaleString('en-US', { timeZone: timezone });
    }
    if (newRow.maxTimestamp) {
      const date = new Date(newRow.maxTimestamp);
      newRow.maxTimestamp = date.toLocaleString('en-US', { timeZone: timezone });
    }

    return newRow;
  });

  // Rearrange and rename the columns based on columnTitles
  const arrangedData = processedData.map(row => {
    const arrangedRow = {};
    columnTitles.forEach(col => {
      arrangedRow[col.title] = row[col.key] !== undefined ? row[col.key] : '';
    });
    return arrangedRow;
  });

  // Convert the arranged data to CSV using PapaParse
  const csv = Papa.unparse(arrangedData);

  // Create a Blob from the CSV string
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

  // Trigger the download using FileSaver
  saveAs(blob, `${fileName}.csv`);
}